import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

const ViewCertificatModal = ({ isOpen, certificat, onClose, onDownload }) => {
  const { isDarkMode } = useTheme();

  if (!isOpen || !certificat) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Remove color backgrounds in dark mode, use only neutral grays
  const getCertificatType = (pieceName) => {
    if (!pieceName) return {
      type: 'Document',
      icon: 'fas fa-file',
      headerClass: isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-gray-600 to-gray-700',
      bgClass: isDarkMode ? 'bg-gray-800' : 'bg-gray-100',
      textClass: isDarkMode ? 'text-gray-200' : 'text-gray-800',
      borderClass: isDarkMode ? 'border-gray-700' : 'border-gray-200'
    };

    const name = pieceName.toLowerCase();
    if (name.includes('certificat')) {
      return {
        type: 'Certificat',
        icon: 'fas fa-certificate',
        headerClass: isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-green-600 to-green-700',
        bgClass: isDarkMode ? 'bg-gray-800' : 'bg-green-100',
        textClass: isDarkMode ? 'text-gray-200' : 'text-green-800',
        borderClass: isDarkMode ? 'border-gray-700' : 'border-green-200'
      };
    } else if (name.includes('rapport')) {
      return {
        type: 'Rapport',
        icon: 'fas fa-file-alt',
        headerClass: isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-blue-600 to-blue-700',
        bgClass: isDarkMode ? 'bg-gray-800' : 'bg-blue-100',
        textClass: isDarkMode ? 'text-gray-200' : 'text-blue-800',
        borderClass: isDarkMode ? 'border-gray-700' : 'border-blue-200'
      };
    } else if (name.includes('pdf')) {
      return {
        type: 'PDF',
        icon: 'fas fa-file-pdf',
        headerClass: isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-red-600 to-red-700',
        bgClass: isDarkMode ? 'bg-gray-800' : 'bg-red-100',
        textClass: isDarkMode ? 'text-gray-200' : 'text-red-800',
        borderClass: isDarkMode ? 'border-gray-700' : 'border-red-200'
      };
    }
    return {
      type: 'Document',
      icon: 'fas fa-file',
      headerClass: isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-gray-600 to-gray-700',
      bgClass: isDarkMode ? 'bg-gray-800' : 'bg-gray-100',
      textClass: isDarkMode ? 'text-gray-200' : 'text-gray-800',
      borderClass: isDarkMode ? 'border-gray-700' : 'border-gray-200'
    };
  };

  const certType = getCertificatType(certificat.pieceName);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden`}>
        {/* Header */}
        <div className={`px-6 py-4 ${certType.headerClass}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`w-12 h-12 ${isDarkMode ? 'bg-gray-700' : 'bg-white bg-opacity-20'} rounded-xl flex items-center justify-center mr-4`}>
                <i className={`${certType.icon} text-white text-xl`}></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {certificat.pieceName || 'Document sans nom'}
                </h2>
                <p className="text-white text-opacity-90 text-sm">
                  {certType.type} • ID: {certificat.id}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {certificat.piecePath && (
                <button
                  onClick={() => onDownload(certificat)}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                >
                  <i className="fas fa-download mr-2"></i>
                  Télécharger
                </button>
              )}
              <button
                onClick={onClose}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(95vh-100px)]">
          <div className="p-6 space-y-6">
            {/* Main Information Card */}
            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6`}>
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center">
                  <div className={`p-3 ${isDarkMode ? 'bg-gray-800' : 'bg-primary-100'} rounded-xl mr-4`}>
                    <i className={`fas fa-info-circle ${isDarkMode ? 'text-primary-300' : 'text-primary-600'} text-xl`}></i>
                  </div>
                  <div>
                    <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Informations générales</h3>
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Détails du certificat</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} rounded-lg p-4 border`}>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Identifiant</label>
                    <div className="flex items-center">
                      <i className="fas fa-hashtag text-gray-400 mr-2"></i>
                      <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>#{certificat.id}</span>
                    </div>
                  </div>

                  <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} rounded-lg p-4 border`}>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Nom du document</label>
                    <div className="flex items-center">
                      <i className={`${certType.icon} text-gray-400 mr-2`}></i>
                      <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {certificat.pieceName || 'Document sans nom'}
                      </span>
                    </div>
                  </div>

                  <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} rounded-lg p-4 border`}>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type de document</label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 border border-gray-600' : `${certType.bgClass} ${certType.textClass} border ${certType.borderClass}`}`}>
                      <div className="w-2 h-2 rounded-full bg-current mr-2"></div>
                      {certType.type}
                    </span>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} rounded-lg p-4 border`}>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Date de certification</label>
                    <div className="flex items-center">
                      <i className="fas fa-calendar text-gray-400 mr-2"></i>
                      <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatDate(certificat.dateCertif)}
                      </span>
                    </div>
                  </div>

                  <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} rounded-lg p-4 border`}>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Chemin du fichier</label>
                    <div className="flex items-center">
                      <i className="fas fa-folder text-gray-400 mr-2"></i>
                      <span className={`text-lg font-semibold break-all ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {certificat.piecePath || 'Non spécifié'}
                      </span>
                    </div>
                  </div>

                  <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} rounded-lg p-4 border`}>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Statut du fichier</label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      certificat.piecePath 
                        ? (isDarkMode ? 'bg-gray-700 text-green-300 border border-gray-600' : 'bg-green-100 text-green-800 border border-green-200')
                        : (isDarkMode ? 'bg-gray-700 text-gray-200 border border-gray-600' : 'bg-gray-100 text-gray-800 border border-gray-200')
                    }`}>
                      <div className="w-2 h-2 rounded-full bg-current mr-2"></div>
                      {certificat.piecePath ? 'Fichier disponible' : 'En attente'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Client Information */}
            <div className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'} rounded-xl p-6 border`}>
              <div className="flex items-center mb-6">
                <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-blue-200'} p-3 rounded-xl mr-4`}>
                  <i className="fas fa-building text-blue-200 text-xl"></i>
                </div>
                <div>
                  <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Informations client</h3>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Détails du client associé</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-blue-200'} rounded-lg p-4 border`}>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Client ID</label>
                  <div className="flex items-center">
                    <i className="fas fa-hashtag text-gray-400 mr-2"></i>
                    <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>#{certificat.clientId}</span>
                  </div>
                </div>

                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-blue-200'} rounded-lg p-4 border`}>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Raison sociale</label>
                  <div className="flex items-center">
                    <i className="fas fa-building text-gray-400 mr-2"></i>
                    <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {certificat.raisonSocial || 'Non spécifiée'}
                    </span>
                  </div>
                </div>

                {(certificat.clientFirstName || certificat.clientLastName) && (
                  <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-blue-200'} rounded-lg p-4 border md:col-span-2`}>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Contact</label>
                    <div className="flex items-center">
                      <i className="fas fa-user text-gray-400 mr-2"></i>
                      <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {certificat.clientFirstName} {certificat.clientLastName}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* File Actions */}
            {certificat.piecePath && (
              <div className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-green-50 border-green-200'} rounded-xl p-6 border`}>
                <div className="flex items-center mb-4">
                  <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-green-200'} p-3 rounded-xl mr-4`}>
                    <i className="fas fa-file-download text-green-200 text-xl"></i>
                  </div>
                  <div>
                    <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Actions sur le fichier</h3>
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Options de téléchargement et d'aperçu</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => onDownload(certificat)}
                    className={`${isDarkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-green-600 text-white hover:bg-green-700'} px-6 py-3 rounded-lg transition-colors font-medium`}
                  >
                    <i className="fas fa-download mr-2"></i>
                    Télécharger le fichier
                  </button>
                  
                  <button
                    className={`${isDarkMode ? 'bg-gray-800 text-gray-200 border border-gray-600 hover:bg-gray-700' : 'bg-white text-green-700 border border-green-300 hover:bg-green-50'} px-6 py-3 rounded-lg transition-colors font-medium`}
                  >
                    <i className="fas fa-eye mr-2"></i>
                    Aperçu (bientôt disponible)
                  </button>
                </div>

                <div className={`mt-4 p-4 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-200' : 'bg-white border-green-200 text-gray-600'}`}>
                  <div className="flex items-center text-sm">
                    <i className="fas fa-info-circle mr-2"></i>
                    <span>
                      <strong>Chemin du fichier:</strong> {certificat.piecePath}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`border-t px-6 py-4 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <i className="fas fa-clock mr-1"></i>
              Créé le {formatDate(certificat.dateCertif)}
            </div>
            <div className="flex space-x-3">
              {certificat.piecePath && (
                <button
                  onClick={() => onDownload(certificat)}
                  className={`${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-green-600 text-white hover:bg-green-700'} px-4 py-2 rounded-lg transition-colors font-medium text-sm`}
                >
                  <i className="fas fa-download mr-2"></i>
                  Télécharger
                </button>
              )}
              <button
                onClick={onClose}
                className={`${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-600 text-white hover:bg-gray-700'} px-6 py-2 rounded-lg transition-colors font-medium text-sm`}
              >
                <i className="fas fa-times mr-2"></i>
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewCertificatModal;